import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { TenantConfig, TenantColors, TenantsFile } from '../interfaces/tenant.interface';
import { ThemeService } from './theme.service';

@Injectable({
  providedIn: 'root'
})
export class TenantService {
  private tenants: TenantConfig[] = [];
  private currentTenantSubject = new BehaviorSubject<TenantConfig | null>(null);
  private loaded = false;

  tenant$ = this.currentTenantSubject.asObservable();

  constructor(
    private http: HttpClient,
    private themeService: ThemeService
  ) {
    this.themeService.theme$.subscribe(theme => {
      const currentTenant = this.currentTenantSubject.value;
      if (currentTenant) {
        const c = theme === 'dark' ? currentTenant.darkColors : currentTenant.colors;
        this.setCssProperties(c);
      }
    });
  }

  get currentTenant(): TenantConfig | null {
    return this.currentTenantSubject.value;
  }

  get currentSlug(): string {
    return this.currentTenantSubject.value?.slug ?? '';
  }

  loadTenants(): Observable<TenantConfig[]> {
    if (this.loaded) {
      return of(this.tenants);
    }

    return this.http.get<TenantsFile>('/api/tenants/config').pipe(
      map(data => data.tenants),
      tap(tenants => {
        this.tenants = tenants;
        this.loaded = true;
      }),
      catchError(() => {
        this.loaded = true;
        return of([]);
      })
    );
  }

  refresh(): Observable<TenantConfig[]> {
    this.loaded = false;
    return this.loadTenants().pipe(
      tap(tenants => {
        const activeSlug = this.currentSlug;
        const updated = tenants.find(t => t.slug === activeSlug);
        if (updated) {
          this.setTenant(updated);
        }
      })
    );
  }

  resolve(slug: string): Observable<boolean> {
    return this.loadTenants().pipe(
      map(tenants => {
        const tenant = tenants.find(t => t.slug === slug);
        if (tenant) {
          this.setTenant(tenant);
          return true;
        }
        return false;
      })
    );
  }

  setDefault(): void {
    const defaultTenant = this.tenants.find(t => t.slug === '');
    if (defaultTenant) {
      this.setTenant(defaultTenant);
    }
  }

  isValidSlug(slug: string): boolean {
    return this.tenants.some(t => t.slug === slug);
  }

  getRoutePrefix(): string {
    const slug = this.currentSlug;
    return slug ? `/${slug}` : '';
  }

  buildRoute(commands: string | any[]): any[] {
    const prefix = this.getRoutePrefix();
    
    if (typeof commands === 'string') {
      const path = commands.startsWith('/') ? commands : `/${commands}`;
      return [prefix ? `${prefix}${path}` : path];
    }
    
    if (Array.isArray(commands)) {
      const newCommands = [...commands];
      if (prefix && newCommands.length > 0 && typeof newCommands[0] === 'string') {
        if (newCommands[0].startsWith('/')) {
          newCommands[0] = `${prefix}${newCommands[0]}`;
        } else if (!newCommands[0].startsWith('./') && !newCommands[0].startsWith('../')) {
          newCommands[0] = `${prefix}/${newCommands[0]}`;
        }
      }
      return newCommands;
    }
    
    return [];
  }

  private setTenant(tenant: TenantConfig): void {
    this.currentTenantSubject.next(tenant);
    this.applyBranding(tenant);
  }

  private applyBranding(tenant: TenantConfig): void {
    const isDark = this.themeService.currentTheme === 'dark';
    const colors = isDark ? tenant.darkColors : tenant.colors;
    this.setCssProperties(colors);
  }

  private setCssProperties(colors: TenantColors): void {
    const root = document.body;
    root.style.setProperty('--accent-color', colors.accentColor);
    root.style.setProperty('--bg-selected', colors.bgSelected);
    root.style.setProperty('--button-bg', colors.buttonBg);
    root.style.setProperty('--button-hover-bg', colors.buttonHoverBg);
    root.style.setProperty('--button-border', colors.buttonBorder);

    const accent = colors.accentColor;
    root.style.setProperty('--accent-focus-shadow', `0 0 0 2px ${this.hexToRgba(accent, 0.1)}`);
  }

  private hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
}
